import { Badge, ListRow, PresenceAvatar } from "@somethings-ui/web";
import { useEffect, useRef } from "react";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { cn } from "@/lib/utils";
import type { NoticeConversation } from "./notice-conversations";
import { formatRelativeNoticeTimestamp } from "./notice-timestamp";

function noticeRowAriaLabel(
  name: string,
  unread: boolean,
  needsResponse: boolean,
): string {
  const status = [
    unread ? "unread" : null,
    needsResponse ? "needs response" : null,
  ]
    .filter((part): part is string => part !== null)
    .join(", ");
  return status.length > 0 ? `${name}, ${status}` : name;
}

interface NoticeConversationRowProps {
  conversation: NoticeConversation;
  collapsed: boolean;
  selected: boolean;
  showBadge: boolean;
  onSelect: (id: string) => void;
  onBadgeImpression?: () => void;
}

export function NoticeConversationRow({
  conversation,
  collapsed,
  selected,
  showBadge,
  onSelect,
  onBadgeImpression,
}: NoticeConversationRowProps) {
  const {
    id,
    name,
    preview,
    lastMessageAt,
    online,
    unread,
    needsResponse,
    badge,
    avatarUrl,
  } = conversation;
  const timestamp = formatRelativeNoticeTimestamp(lastMessageAt);
  const { url: signedAvatarUrl } = useSignedMedia(avatarUrl);

  // Report a single impression the first time this row's badge becomes visible.
  // Rows are keyed by conversation id, so a workspace switch remounts them and
  // naturally re-arms the impression.
  const badgeVisible = showBadge && badge !== null;
  const impressionFired = useRef(false);
  useEffect(() => {
    if (!badgeVisible || impressionFired.current) return;
    impressionFired.current = true;
    onBadgeImpression?.();
  }, [badgeVisible, onBadgeImpression]);

  return (
    <ListRow
      collapsed={collapsed}
      selected={selected}
      unread={unread}
      onClick={() => onSelect(id)}
      aria-label={noticeRowAriaLabel(name, unread, needsResponse)}
      className={cn(
        "min-h-[64px]",
        selected ? undefined : "hover:bg-surfaces-z4",
      )}
    >
      <ListRow.Leading>
        <PresenceAvatar
          online={online}
          name={name}
          imageUrl={signedAvatarUrl}
          size="sm"
        />
      </ListRow.Leading>
      <ListRow.Content>
        <ListRow.Title>{name}</ListRow.Title>
        <ListRow.Subtitle>{preview}</ListRow.Subtitle>
      </ListRow.Content>
      <ListRow.Trailing>
        {showBadge && badge !== null ? (
          <Badge variant="brand">{badge.label}</Badge>
        ) : null}
        <span className="pt-0.5 font-body text-body-3 leading-[1.4] text-text-secondary">
          {timestamp}
        </span>
      </ListRow.Trailing>
    </ListRow>
  );
}
